import React, { PropsWithChildren, useEffect, useState, useRef, useCallback } from 'react';
import { Typography } from '@material-ui/core';
import { Sidebar, SidebarPage } from '@backstage/core-components';
import { Link, useLocation } from 'react-router-dom';
import { configApiRef, identityApiRef, useApi } from '@backstage/core-plugin-api';
import { TourProvider, useTour } from '../tour';
import NavigateBeforeIcon from '@material-ui/icons/NavigateBefore';
import NavigateNextIcon from '@material-ui/icons/NavigateNext';
import HomeIcon from '../../assets/HomeIcon';
import CatalogIcon from '../../assets/CatalogIcon';
// import APIDocs from '../../assets/APIDocs'; // only used by the hidden 'APIs' nav item below
import DocsIcon from '../../assets/DocsIcon';
import TemplatesIcon from '../../assets/TemplatesIcon';
import CloudRequest from '../../assets/CloudRequets';
import TechRadarIcon from '../../assets/TechRadarIcon';
import DeployIcon from '../../assets/DeployIcon';
import CostExplorerIcon from '../../assets/CostExplorerIcon';

/* ── Types ── */

interface DevHubNavItem {
  label: string;
  href: string;
  hrefWithParams?: string;
  icon: React.ReactNode;
  matchExact?: boolean;
  matchType?: string;
}

/* ── Config ── */

const devHubNavItems: DevHubNavItem[] = [
  { label: 'Home', href: '/', icon: <HomeIcon />, matchExact: true },
  { label: 'Catalog', href: '/catalog', icon: <CatalogIcon /> },
  // { label: 'APIs', href: '/api-docs', icon: <APIDocs /> },
  { label: 'Docs', href: '/docs', icon: <DocsIcon /> },
  {
    label: 'DevKits',
    href: '/devkits',
    hrefWithParams: '/devkits?filters[type]=service&filters[type]=website',
    icon: <TemplatesIcon />,
    matchType: 'devkits',
  },
  {
    label: 'Requests',
    href: '/requests',
    hrefWithParams: '/requests?filters[type]=automation&filters[type]=request',
    icon: <CloudRequest />,
    matchType: 'requests',
  },
  { label: 'Track Requests', href: '/request-tracking', icon: <CloudRequest /> },
  // { label: 'Tasks', href: '/tasks', icon: <DocsIcon /> },
  { label: 'Deployments', href: '/deployments', icon: <DeployIcon /> },
  { label: 'Cost Explorer', href: '/cost-explorer', icon: <CostExplorerIcon /> },
  // { label: 'AutoGenDoc', href: '/auto-gen-doc', icon: <DocsIcon /> },
  { label: 'Radar', href: '/tech-radar', icon: <TechRadarIcon /> },
];

/* ── Helpers ── */

/**
 * Detect whether we're loaded inside the Project Estimator iframe.
 * The parent adds ?embedded=1 to signal this.
 */
function useIsEmbedded(): boolean {
  const [embedded, setEmbedded] = useState(false);
  useEffect(() => {
    // Check the query-param first (reliable), then fall back to frame detection
    const params = new URLSearchParams(window.location.search);
    const isEmbedded =
      params.get('embedded') === '1' || window.self !== window.top;
    setEmbedded(isEmbedded);
  }, []);
  return embedded;
}

/* ── Tour help button (must live inside TourProvider) ── */

function TourHelpButton() {
  const { startTour } = useTour();
  return (
    <button
      id="tour-help-btn"
      className="av-floating-help-btn"
      onClick={startTour}
      title="Take a guided tour"
    >
      <svg
        width="32"
        height="32"
        viewBox="0 0 24 24"
        fill="none"
        stroke="white"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
      </svg>
    </button>
  );
}

/* ── Component ── */

const RootContent = ({ children }: PropsWithChildren<{}>) => {
  const { pathname: currentPagePath } = useLocation();
  const apiClient = useApi(identityApiRef);
  const configApi = useApi(configApiRef);
  const appBaseUrl = configApi.getString('app.baseUrl');
  const isEmbedded = useIsEmbedded();
  const [userInfo, setUserInfo] = useState({
    name: '',
    profileImage: '',
    email: '',
  });
  /* ── Scrolling Logic ── */
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScroll = useCallback(() => {
    const el = scrollContainerRef.current;
    if (el) {
      const { scrollLeft, scrollWidth, clientWidth } = el;
      // Use a slightly larger threshold (2px) to account for rounding
      setCanScrollLeft(scrollLeft > 2);
      setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 2);
    }
  }, []);

  const handleScroll = (direction: 'left' | 'right') => {
    const el = scrollContainerRef.current;
    if (el) {
      const scrollAmount = el.clientWidth * 0.8;
      el.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
      // Short delay check to update arrow visibility after smooth scroll starts
      setTimeout(checkScroll, 100);
      setTimeout(checkScroll, 500);
    }
  };

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return undefined;

    checkScroll();

    // ResizeObserver tracks container size and content size
    const resizeObserver = new ResizeObserver(() => {
      checkScroll();
      // Second check to ensure layout has settled
      requestAnimationFrame(checkScroll);
    });
    
    resizeObserver.observe(el);
    if (el.parentElement) {
      resizeObserver.observe(el.parentElement);
    }

    window.addEventListener('resize', checkScroll);

    // Multiple polls to catch animation completion
    const timers = [100, 300, 600, 1000].map(t => setTimeout(checkScroll, t));

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', checkScroll);
      timers.forEach(clearTimeout);
    };
  }, [checkScroll]);

  /**
   * When embedded inside the Estimator iframe, post navigation changes
   * to the parent window so it can update the URL bar for deep-linking.
   */
  useEffect(() => {
    if (!isEmbedded) return;
    try {
      window.parent.postMessage(
        { type: 'devhub-navigation', path: currentPagePath },
        '*', // parent origin validated on the receiver side
      );
    } catch {
      // postMessage may fail in cross-origin edge-cases; silently ignore
    }
  }, [isEmbedded, currentPagePath]);

  // Toggle a CSS class on <body> so styles.css can adapt when embedded
  useEffect(() => {
    if (isEmbedded) {
      document.body.classList.add('embedded-mode');
    }
    return () => {
      document.body.classList.remove('embedded-mode');
    };
  }, [isEmbedded]);

  useEffect(() => {
    (async () => {
      const user = await apiClient.getProfileInfo();
      setUserInfo({
        name: user.displayName || '',
        profileImage: user.picture || '',
        email: user.email || '',
      });
    })();
  }, []);

  // Track the scaffolder origin so template sub-routes keep the right tab active.
  // When the user clicks DevKits or Requests, we store the origin path.
  // Backstage may resolve template sub-routes to the last-mounted ScaffolderPage,
  // so we use sessionStorage to remember which tab was the entry point.
  useEffect(() => {
    if (currentPagePath === '/devkits' || currentPagePath.startsWith('/devkits?')) {
      sessionStorage.setItem('scaffolder-origin', 'devkits');
    } else if (currentPagePath === '/requests' || currentPagePath.startsWith('/requests?')) {
      sessionStorage.setItem('scaffolder-origin', 'requests');
    }
  }, [currentPagePath]);

  const isDevHubNavActive = (item: DevHubNavItem) => {
    if (item.matchExact) return currentPagePath === item.href;

    const scaffolderOrigin = sessionStorage.getItem('scaffolder-origin');
    const isOnScaffolderSubRoute =
      currentPagePath.startsWith('/devkits/') || currentPagePath.startsWith('/requests/');

    if (item.matchType === 'devkits') {
      if (isOnScaffolderSubRoute) return scaffolderOrigin === 'devkits';
      return currentPagePath === '/devkits' || currentPagePath.startsWith('/devkits?');
    }
    if (item.matchType === 'requests') {
      if (isOnScaffolderSubRoute) return scaffolderOrigin === 'requests';
      return currentPagePath === '/requests' || currentPagePath.startsWith('/requests?');
    }
    return currentPagePath.startsWith(item.href);
  };

  const renderNavPills = () => (
    <div className="av-nav-scroller-container">
      {canScrollLeft && (
        <button
          className="av-nav-arrow av-nav-arrow--left"
          onClick={() => handleScroll('left')}
          title="Scroll Left"
        >
          <NavigateBeforeIcon />
        </button>
      )}

      <div
        className="av-nav-scroll-content"
        ref={scrollContainerRef}
        onScroll={checkScroll}
      >
        {devHubNavItems.map(sub => {
          const active = isDevHubNavActive(sub);
          const tourId = `tour-nav-${sub.label.toLowerCase().replaceAll(/\s+/g, '-').replaceAll(/[^a-z0-9-]/g, '')}`;
          return (
            <Link
              key={sub.label}
              id={tourId}
              to={sub.hrefWithParams || sub.href}
              className={`av-sub-pill ${active ? 'av-sub-pill--active' : ''}`}
            >
              <span className="av-sub-pill__icon">{sub.icon}</span>
              {sub.label}
            </Link>
          );
        })}
      </div>

      {canScrollRight && (
        <button
          className="av-nav-arrow av-nav-arrow--right"
          onClick={() => handleScroll('right')}
          title="Scroll Right"
        >
          <NavigateNextIcon />
        </button>
      )}
    </div>
  );

  // When embedded in the Estimator iframe, render only the content —
  // the parent app provides its own header.
  if (isEmbedded) {
    return (
      <SidebarPage>
        <Sidebar>
          <div style={{ display: 'none' }} />
        </Sidebar>

        <div className="av-shell" style={{ background: 'transparent' }}>
          <main className="av-content">
            {children}
          </main>
        </div>
        <TourHelpButton />
      </SidebarPage>
    );
  }

  return (
    <SidebarPage>
      {/* Backstage native sidebar — kept hidden for compat */}
      <Sidebar>
        <div style={{ display: 'none' }} />
      </Sidebar>

      <div className="av-shell">
        {/* ═══════════════ Top navbar ═══════════════ */}
        <header className="av-topbar">
          {/* Logo */}
          <a id="tour-logo" href={appBaseUrl} className="av-logo-pill">
            <Typography
              style={{
                fontFamily: '"Work Sans", sans-serif',
                fontWeight: 500,
                fontSize: '1.375rem',
                color: '#1a1a1a',
                lineHeight: 1,
              }}
            >
              <span style={{ color: '#F58321' }}>DevHub</span>
            </Typography>
          </a>

          {/* Center — pill nav with inline DevHub expansion */}
          <div id="tour-nav-pills" className="av-nav-pills">
            {renderNavPills()}
          </div>


          {/* Right — icon buttons */}
          <div className="av-topbar__right">
            <Link id="tour-notifications" to="/notifications" style={{ textDecoration: 'none' }}>
              <div className="av-icon-btn">
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                  <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                </svg>
              </div>
            </Link>
            <Link id="tour-profile" to="/settings" style={{ textDecoration: 'none' }}>
              <div className="av-icon-btn">
                {userInfo.profileImage ? (
                  <div className="av-profile-img">
                    <img alt="Profile" src={userInfo.profileImage} />
                  </div>
                ) : (
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                )}
              </div>
            </Link>
          </div>
        </header>

        {/* ═══════════════ Page content ═══════════════ */}
        <main className="av-content">{children}</main>
      </div>
      <TourHelpButton />
    </SidebarPage>
  );
};

export const Root = ({ children }: PropsWithChildren<{}>) => (
  <TourProvider>
    <RootContent>{children}</RootContent>
  </TourProvider>
);


